import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ADMIN_API } from '../../context/AdminAuthContext';
import {
  Users, Search, Check, X, Clock, ChevronDown,
  Building, Phone, Mail, MapPin, Briefcase, Trash2, AlertTriangle
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'Tous les statuts' },
  { value: 'pending', label: 'En attente' },
  { value: 'approved', label: 'Validés' },
  { value: 'rejected', label: 'Refusés' },
];

const statusBadge = (status) => {
  const cfg = {
    approved: 'bg-green-500/20 text-green-400 border-green-500/30',
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  const labels = { approved: 'Validé', pending: 'En attente', rejected: 'Refusé' };
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${cfg[status] || cfg.pending}`}>
      {labels[status] || status}
    </span>
  );
};

const ConfirmModal = ({ user, onConfirm, onCancel, action }) => (
  <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
    <div className="bg-gray-800 rounded-2xl border border-gray-700 p-6 max-w-sm w-full">
      <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
        <AlertTriangle size={24} className="text-red-400" />
      </div>
      <h3 className="text-white font-bold text-center mb-2">
        {action === 'delete' ? 'Supprimer ce compte ?' : 'Confirmer'}
      </h3>
      <p className="text-gray-400 text-sm text-center mb-6">
        {action === 'delete'
          ? `Cette action est irréversible. Le compte de ${user?.prenom} ${user?.nom} sera définitivement supprimé.`
          : `Voulez-vous modifier le statut de ${user?.prenom} ${user?.nom} ?`}
      </p>
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 py-2.5 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-700 transition-all text-sm font-semibold">
          Annuler
        </button>
        <button onClick={onConfirm} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-all text-sm font-semibold">
          Confirmer
        </button>
      </div>
    </div>
  </div>
);

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [expandedUser, setExpandedUser] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${ADMIN_API}/users`, {
        params: { status: statusFilter || undefined, limit: 100 },
        withCredentials: true
      });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (userId, newStatus) => {
    setActionLoading(p => ({ ...p, [userId]: true }));
    try {
      const res = await axios.put(`${ADMIN_API}/users/${userId}/status`, { status: newStatus }, { withCredentials: true });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, status: res.data.status } : u));
    } catch (e) { console.error(e); }
    finally { setActionLoading(p => ({ ...p, [userId]: false })); }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${ADMIN_API}/users/${userId}`, { withCredentials: true });
      setUsers(prev => prev.filter(u => u.user_id !== userId));
      setTotal(p => p - 1);
    } catch (e) { console.error(e); }
    finally { setConfirmModal(null); }
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      u.email?.toLowerCase().includes(q) ||
      u.company_name?.toLowerCase().includes(q) ||
      u.nom?.toLowerCase().includes(q) ||
      u.prenom?.toLowerCase().includes(q) ||
      u.siret?.includes(q)
    );
  });

  const pendingCount = users.filter(u => u.status === 'pending').length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-white">Comptes Professionnels</h1>
          <p className="text-gray-400 text-sm mt-0.5">{total} compte{total > 1 ? 's' : ''} au total</p>
        </div>
        {pendingCount > 0 && (
          <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 px-4 py-2 rounded-xl text-sm font-semibold">
            <Clock size={16} /> {pendingCount} en attente de validation
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par nom, email, société, SIRET..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-orange-500 placeholder-gray-500"
          />
        </div>
        <div className="flex gap-2">
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                statusFilter === opt.value
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="space-y-3">
          {Array(5).fill(0).map((_, i) => (
            <div key={i} className="bg-gray-800 h-20 rounded-xl animate-pulse border border-gray-700" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-gray-800 rounded-2xl border border-gray-700 p-12 text-center">
          <Users size={40} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">Aucun compte trouvé</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(user => (
            <div key={user.user_id} className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              {/* Main row */}
              <div className="flex items-center justify-between p-4 gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {/* Avatar */}
                  <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                    {user.picture
                      ? <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                      : <span className="text-orange-400 font-bold text-sm">{(user.prenom || user.name || '?')[0].toUpperCase()}</span>
                    }
                  </div>
                  {/* Info */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-bold text-sm">{user.prenom} {user.nom}</p>
                      {statusBadge(user.status)}
                      {user.auth_type === 'google' && (
                        <span className="text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded-full">Google</span>
                      )}
                    </div>
                    <p className="text-gray-400 text-xs truncate">{user.company_name} — {user.email}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {user.status !== 'approved' && (
                    <button
                      onClick={() => updateStatus(user.user_id, 'approved')}
                      disabled={actionLoading[user.user_id]}
                      className="flex items-center gap-1.5 bg-green-500/10 hover:bg-green-500 border border-green-500/30 text-green-400 hover:text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                    >
                      <Check size={14} /> Valider
                    </button>
                  )}
                  {user.status !== 'rejected' && (
                    <button
                      onClick={() => updateStatus(user.user_id, 'rejected')}
                      disabled={actionLoading[user.user_id]}
                      className="flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500 border border-red-500/30 text-red-400 hover:text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                    >
                      <X size={14} /> Refuser
                    </button>
                  )}
                  <button
                    onClick={() => setExpandedUser(expandedUser === user.user_id ? null : user.user_id)}
                    className="p-1.5 text-gray-500 hover:text-white transition-colors"
                  >
                    <ChevronDown size={16} className={`transition-transform ${expandedUser === user.user_id ? 'rotate-180' : ''}`} />
                  </button>
                  <button
                    onClick={() => setConfirmModal({ user, action: 'delete' })}
                    className="p-1.5 text-gray-600 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Expanded detail */}
              {expandedUser === user.user_id && (
                <div className="border-t border-gray-700 p-4 bg-gray-750">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Building size={14} className="text-gray-500" />
                      <div>
                        <p className="text-gray-500 text-xs">SIRET</p>
                        <p className="text-gray-200 font-mono text-xs">{user.siret || '—'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase size={14} className="text-gray-500" />
                      <div>
                        <p className="text-gray-500 text-xs">Activité</p>
                        <p className="text-gray-200 text-xs">{user.activity || '—'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={14} className="text-gray-500" />
                      <div>
                        <p className="text-gray-500 text-xs">Téléphone</p>
                        <p className="text-gray-200 text-xs">{user.phone || '—'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-gray-500" />
                      <div>
                        <p className="text-gray-500 text-xs">Ville</p>
                        <p className="text-gray-200 text-xs">{user.postal_code} {user.city}</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-xs mt-3">
                    Inscrit le {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          user={confirmModal.user}
          action={confirmModal.action}
          onCancel={() => setConfirmModal(null)}
          onConfirm={() => deleteUser(confirmModal.user.user_id)}
        />
      )}
    </div>
  );
};

export default AdminUsersPage;
